/**
 * Topic Selector Component
 * Allows users to select topics and concepts for their learning session
 */
import React, { useState, useEffect } from 'react';
import APIService from '../services/APIService';

const TopicSelector = ({ onSessionStart, onBack }) => {
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [topics, setTopics] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState(new Set());
  const [concepts, setConcepts] = useState({});
  const [selectedConcepts, setSelectedConcepts] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConceptFilter, setShowConceptFilter] = useState(false);
  const [sessionMode, setSessionMode] = useState('ai'); // 'ai', 'self', 'flashcard', 'voice'
  const [questionCount, setQuestionCount] = useState(10);


  const handleTopicToggle = async (topic) => {
    const newSelected = new Set(selectedTopics);

    if (newSelected.has(topic)) {
      newSelected.delete(topic);
    } else {
      newSelected.add(topic);

      // Fetch concepts for this topic
      try {
        const data = await APIService.getConcepts(topic);
        setConcepts((prev) => ({
          ...prev,
          [topic]: data.concepts || [],
        }));
      } catch (err) {
        console.error(`Failed to fetch concepts for ${topic}:`, err);
      }
    }

    setSelectedTopics(newSelected);
  };

  const handleConceptToggle = (concept) => {
    const newSelected = new Set(selectedConcepts);

    if (newSelected.has(concept)) {
      newSelected.delete(concept);
    } else {
      newSelected.add(concept);
    }

    setSelectedConcepts(newSelected);
  };

  const loadForSubject = async (subject) => {
    try {
      setLoading(true);
      const data = await APIService.loadSubject(subject);
      setTopics(data.topics || []);
      setSelectedTopics(new Set());
      setConcepts({});
      setSelectedConcepts(new Set());
      setLoading(false);
    } catch (err) {
      setError(`Failed to load subject: ${err.message}`);
      setLoading(false);
    }
  };

  const handleStartSession = async () => {
    if (sessionMode === 'chat') {
      onSessionStart({ chat_mode: true });
      return;
    }

    if (selectedTopics.size === 0) {
      setError('Please select at least one topic');
      return;
    }

    try {
      const topicsArray = Array.from(selectedTopics);
      const conceptsArray = selectedConcepts.size > 0 ? Array.from(selectedConcepts) : null;

      // Map session mode to API options
      const options = {
        question_count: sessionMode === 'voice' ? 3 : questionCount,  // Voice interviews use 3 questions
        no_evaluation: sessionMode === 'self',
        flashcard_mode: sessionMode === 'flashcard',
        voice_interview: sessionMode === 'voice',
      };

      const sessionData = await APIService.startSession(topicsArray, conceptsArray, true, options);
      // Ensure routing flags come from local state, not just the backend response
      onSessionStart({ ...sessionData, voice_interview: sessionMode === 'voice' });
    } catch (err) {
      setError(`Failed to start session: ${err.message}`);
    }
  };

  useEffect(() => {
    // Fetch available subjects
    const fetchSubjects = async () => {
      try {
        const data = await APIService.getSubjects();
        setSubjects(data.subjects || []);
        // automatically select first
        if (data.subjects && data.subjects.length > 0) {
          setSelectedSubject(data.subjects[0]);
          await loadForSubject(data.subjects[0]);
        }
        setLoading(false);
      } catch (err) {
        setError(`Failed to load subjects: ${err.message}`);
        setLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  if (loading) {
    return <div className="loading">Loading topics...</div>;
  }

  return (
    <div className="landing-container">
      <div className="landing-header">
        <h1 className="landing-title">Master Your Knowledge</h1>
        <p className="landing-subtitle">
          Select topics, choose your learning style, and begin your personalized learning journey
        </p>
      </div>

      <div className="topic-selector-wrapper">
        {error && (
          <div style={{
            padding: 'var(--space-4)',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--danger)',
            marginBottom: 'var(--space-6)',
            fontSize: 'var(--text-sm)'
          }}>
            {error}
          </div>
        )}

        {/* Subject Selector */}
        {subjects.length > 0 && (
          <div className="selector-section">
            <div className="section-header">
              <h3 className="section-title">📚 Select Subject</h3>
              <span className="section-count">{subjects.length}</span>
            </div>
            <div className="subject-selector-container">
              <select
                className="subject-dropdown"
                value={selectedSubject || ''}
                onChange={(e) => {
                  const subj = e.target.value;
                  setSelectedSubject(subj);
                  loadForSubject(subj);
                }}
              >
                {subjects.map((subj) => (
                  <option key={subj} value={subj}>
                    {subj}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Topics Selection */}
        <div className="selector-section">
          <div className="section-header">
            <h3 className="section-title">🎯 Select Topics</h3>
            <span className="section-count">{selectedTopics.size} selected</span>
          </div>
          <div className="topics-grid">
            {topics.map((topic) => (
              <div key={topic}>
                <label style={{ display: 'block' }}>
                  <div
                    className={`topic-card ${selectedTopics.has(topic) ? 'selected' : ''}`}
                    onClick={() => handleTopicToggle(topic)}
                  >
                    <div className="topic-card-title">{topic}</div>
                    <div className="topic-card-count">
                      {concepts[topic]?.length || 0} concepts
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    className="topic-checkbox"
                    checked={selectedTopics.has(topic)}
                    onChange={() => handleTopicToggle(topic)}
                    style={{ display: 'none' }}
                  />
                </label>

                {/* Concept Filter */}
                {selectedTopics.has(topic) && concepts[topic] && concepts[topic].length > 0 && (
                  <div style={{ marginTop: 'var(--space-4)' }}>
                    <div className="section-header" style={{ marginBottom: 'var(--space-4)' }}>
                      <h4 className="section-title" style={{ fontSize: 'var(--text-base)', gap: 0 }}>
                        🏷️ Filter Concepts
                      </h4>
                    </div>
                    <div className="concepts-filter-container">
                      {concepts[topic].map((concept) => (
                        <label key={concept} className="concept-item">
                          <input
                            type="checkbox"
                            checked={selectedConcepts.has(concept)}
                            onChange={() => handleConceptToggle(concept)}
                          />
                          <span className="concept-label">{concept}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Question Count Selector */}
        {sessionMode !== 'voice' && (
          <div className="selector-section">
            <div className="section-header">
              <h3 className="section-title">❓ Question Quantity</h3>
            </div>
            <div className="question-count-container">
              <label htmlFor="question-count" className="question-count-label">
                Number of Questions:
              </label>
              <input
                id="question-count"
                type="number"
                className="question-count-input"
                min="1"
                max="100"
                value={questionCount}
                onChange={(e) => setQuestionCount(parseInt(e.target.value) || 1)}
              />
              <span className="question-count-info">{questionCount} questions</span>
            </div>
          </div>
        )}
        {sessionMode === 'voice' && (
          <div className="selector-section">
            <div style={{
              padding: 'var(--space-4)',
              background: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--text-secondary)',
              fontSize: 'var(--text-sm)'
            }}>
              ℹ️ Voice interviews always include 3 carefully selected questions for comprehensive assessment
            </div>
          </div>
        )}

        {/* Learning Mode Selection */}
        <div className="selector-section">
          <div className="section-header">
            <h3 className="section-title">🚀 Choose Learning Mode</h3>
          </div>
          <div className="mode-cards-container">
            <button
              className={`mode-card ${sessionMode === 'flashcard' ? 'selected' : ''}`}
              onClick={() => setSessionMode('flashcard')}
              type="button"
            >
              <div className="mode-icon">💡</div>
              <div className="mode-title">Flashcard Mode</div>
              <div className="mode-description">Quick reveal answers to test your knowledge</div>
            </button>

            <button
              className={`mode-card ${sessionMode === 'self' ? 'selected' : ''}`}
              onClick={() => setSessionMode('self')}
              type="button"
            >
              <div className="mode-icon">✍️</div>
              <div className="mode-title">Self Assessment</div>
              <div className="mode-description">Grade your own answers honestly</div>
            </button>

            <button
              className={`mode-card ${sessionMode === 'ai' ? 'selected' : ''}`}
              onClick={() => setSessionMode('ai')}
              type="button"
            >
              <div className="mode-icon">🤖</div>
              <div className="mode-title">AI Evaluation</div>
              <div className="mode-description">AI provides detailed feedback and analysis</div>
            </button>

            <button
              className={`mode-card ${sessionMode === 'voice' ? 'selected' : ''}`}
              onClick={() => setSessionMode('voice')}
              type="button"
            >
              <div className="mode-icon">🎙️</div>
              <div className="mode-title">AI Voice Interview</div>
              <div className="mode-description">Real-time voice conversation with AI interviewer</div>
            </button>

            <button
              className={`mode-card ${sessionMode === 'chat' ? 'selected' : ''}`}
              onClick={() => setSessionMode('chat')}
              type="button"
            >
              <div className="mode-icon">💬</div>
              <div className="mode-title">Free Chat</div>
              <div className="mode-description">Ask anything — streamed AI responses</div>
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="start-session-btn-container">
          <button
            onClick={onBack}
            className="btn-secondary"
            type="button"
          >
            ← Back to Menu
          </button>
          <button
            onClick={handleStartSession}
            className="start-session-btn"
            disabled={sessionMode !== 'chat' && selectedTopics.size === 0}
            type="button"
          >
            {sessionMode === 'chat' ? '💬 Start Free Chat' : '✨ Begin Learning Session'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TopicSelector;
